<script setup lang="ts">
import type { TemplateCardView } from '~/utils/template-catalog'

/**
 * What one search found, across BOTH kinds of image.
 *
 * Search used to be scoped to the open tab, so "wordpress" typed on the
 * operating-system tab answered "nothing found" while WordPress sat one tab
 * away. Results now come grouped — apps, operating-system releases — with the
 * open tab's group first, and picking one from the other group switches the tab
 * (the shell emits the source before the code: the checkout's setter writes to
 * whichever tab is open).
 *
 * An empty result is never a dead end: clear the search, browse everything, or
 * install anything at all with Docker.
 */
const props = defineProps<{
  apps: TemplateCardView[]
  systems: TemplateCardView[]
  /** The open tab: its group is listed first. */
  source: 'os' | 'marketplace'
  query: string
  modelValue: string | null
  /** Docker CE, when the catalog sells it: the answer to "you do not have X". */
  docker: TemplateCardView | null
}>()

const emit = defineEmits<{
  select: [code: string, kind: 'app' | 'os']
  clear: []
  browseAll: []
}>()

const { t } = useI18n()

const groups = computed(() => {
  const apps = {
    key: 'app' as const,
    label: t('billing.page.checkout.os.catalog.search.groupApps'),
    views: props.apps,
  }
  const systems = {
    key: 'os' as const,
    label: t('billing.page.checkout.os.catalog.search.groupOs'),
    views: props.systems,
  }
  return (props.source === 'os' ? [systems, apps] : [apps, systems]).filter((group) => group.views.length > 0)
})

const container = ref<HTMLElement | null>(null)
const roving = useRovingGrid(container, {
  ids: () => groups.value.flatMap((group) => group.views.map((view) => view.code)),
  preferred: () => props.modelValue,
})

function segments(name: string) {
  return highlightSegments(name, props.query)
}
</script>

<template>
  <div data-testid="template-results">
    <div
      v-if="groups.length"
      ref="container"
      class="space-y-5"
      @keydown="roving.onKeydown"
      @focusin="roving.onFocusin"
    >
      <section v-for="group in groups" :key="group.key" class="space-y-2">
        <h3
          :id="`template-results-${group.key}`"
          class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted"
        >
          {{ group.label }}
          <span class="font-normal tabular-nums text-muted">{{ group.views.length }}</span>
        </h3>

        <div
          v-if="group.key === 'app'"
          role="radiogroup"
          :aria-labelledby="`template-results-${group.key}`"
          class="grid grid-cols-1 gap-2.5 @md:grid-cols-2 @4xl:grid-cols-3"
        >
          <CheckoutTemplatePickerAppCard
            v-for="view in group.views"
            :key="view.code"
            :view="view"
            :selected="modelValue === view.code"
            :query="query"
            :tabindex="roving.tabStop.value === view.code ? 0 : -1"
            @select="emit('select', view.code, 'app')"
          />
        </div>

        <!-- Releases, one row each: the search already chose the distribution. -->
        <div
          v-else
          role="radiogroup"
          :aria-labelledby="`template-results-${group.key}`"
          class="grid grid-cols-1 gap-2 @2xl:grid-cols-2"
        >
          <button
            v-for="view in group.views"
            :key="view.code"
            type="button"
            role="radio"
            data-testid="os-search-result"
            :data-template-code="view.code"
            :data-roving-id="view.code"
            :aria-checked="modelValue === view.code"
            :tabindex="roving.tabStop.value === view.code ? 0 : -1"
            :class="[
              'flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              modelValue === view.code
                ? 'border-primary bg-primary/5 ring-1 ring-primary on-tint dark:bg-primary/10'
                : 'border-default bg-default hover:border-accented hover:bg-elevated/40',
            ]"
            @click="emit('select', view.code, 'os')"
          >
            <TemplateLogo :icon-url="view.template.icon_url" :fallback="view.fallbackIcon" size="sm" />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-semibold">
                <template v-for="(segment, index) in segments(view.template.name)" :key="index">
                  <mark
                    v-if="segment.match"
                    class="rounded-[3px] bg-primary/15 text-inherit dark:bg-primary/25"
                  >{{ segment.text }}</mark>
                  <template v-else>{{ segment.text }}</template>
                </template>
              </span>
              <span class="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
                <span>{{ view.groupLabel }} {{ view.version }}</span>
                <UBadge v-if="view.template.is_lts" color="primary" variant="subtle" size="sm">
                  {{ $t('billing.page.checkout.os.lts') }}
                </UBadge>
                <UBadge v-if="view.template.is_recommended" color="success" variant="subtle" size="sm">
                  {{ $t('billing.page.checkout.os.recommended') }}
                </UBadge>
                <span
                  v-if="view.template.support_status === 'ended'"
                  class="inline-flex items-center gap-1 text-error-700 dark:text-error-400"
                >
                  <UIcon name="i-lucide-shield-off" class="size-3" />
                  {{ $t('billing.page.checkout.os.support.ended') }}
                </span>
                <span v-if="view.shortfall" class="inline-flex items-center gap-1 text-warning-700 dark:text-warning-400">
                  <UIcon name="i-lucide-alert-triangle" class="size-3" />
                  {{ $t('billing.page.checkout.os.catalog.card.needs', { spec: specLabel(view.shortfall) }) }}
                </span>
              </span>
            </span>
            <UIcon
              :name="modelValue === view.code ? 'i-lucide-circle-check-big' : 'i-lucide-circle'"
              :class="modelValue === view.code ? 'size-5 shrink-0 text-primary' : 'size-5 shrink-0 text-muted/50'"
            />
          </button>
        </div>
      </section>
    </div>

    <div
      v-else
      data-testid="template-results-empty"
      class="rounded-xl border border-dashed border-default px-4 py-8 text-center"
    >
      <UIcon name="i-lucide-search-x" class="mx-auto size-8 text-muted" />
      <p class="mt-2 text-sm font-medium">
        {{ $t('billing.page.checkout.os.catalog.search.emptyTitle', { query }) }}
      </p>
      <p class="mx-auto mt-1 max-w-md text-xs text-muted">
        {{ $t('billing.page.checkout.os.catalog.search.emptyHint') }}
      </p>
      <div class="mt-4 flex flex-wrap justify-center gap-2">
        <UButton color="neutral" variant="outline" size="sm" icon="i-lucide-x" @click="emit('clear')">
          {{ $t('billing.page.checkout.os.clearSearch') }}
        </UButton>
        <UButton color="neutral" variant="outline" size="sm" icon="i-lucide-layout-grid" @click="emit('browseAll')">
          {{ $t('billing.page.checkout.os.catalog.search.browseAll') }}
        </UButton>
      </div>
      <div v-if="docker" class="mx-auto mt-5 max-w-sm space-y-2 text-left">
        <p class="text-xs text-muted">{{ $t('billing.page.checkout.os.catalog.search.dockerHint') }}</p>
        <div role="radiogroup" :aria-label="$t('billing.page.checkout.os.catalog.search.dockerTitle')">
          <CheckoutTemplatePickerAppCard
            :view="docker"
            :selected="modelValue === docker.code"
            @select="emit('select', docker.code, 'app')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
